/**
 * Section-seed handshake: the ONE-SHOT sessionStorage channel the aircraft control-surface (CS)
 * designer uses to hand a solved 2-D cut section to the high-lift foil tool (FXC control-surface
 * fold, B4 tranche).
 *
 * The CS designer publishes a {@link SlottedSection} (wing box + deflected control surface, in
 * section units) just before routing the user to the high-lift surface; the high-lift surface
 * consumes it ONCE on mount (consume clears the key, so a reload never re-applies a stale seed).
 * sessionStorage (not localStorage) keeps the handoff scoped to the tab — a seed never leaks
 * across windows or survives the session.
 *
 * All storage access is try/caught: SSR (no `window`), private-mode quota errors and malformed
 * JSON all degrade to a silent no-op / `null` — the handshake is best-effort by design.
 */
import type { SlottedSection } from '../geometry/slottedSection';
import { slotMetrics, type SlotMetrics } from '../geometry/gapMetrics';
import type { HighLiftAirfoil, V2 } from '../geometry/estolConfig';

/** Versioned storage key — bump the suffix if the seed record shape ever changes. */
const SECTION_SEED_KEY = 'thread.cs.sectionSeed.v1';
/** The APPLIED seed (post-consume). The workbench surface can be REMOUNTED at any time by the
 *  shell (observed live: the mount-default design silently replaced the seeded one - the one-shot
 *  handshake key was already gone and every piece of in-memory state died with the old instance).
 *  This record makes the seed remount-proof: the intake re-applies from it whenever the primary
 *  key is empty. It is cleared by exactly the actions that already abandon a seed - replacing the
 *  base foil, loading a saved geometry, or the ?geometry= deep link. */
const APPLIED_SEED_KEY = 'thread.cs.sectionSeed.v1.applied';

/**
 * The CLEAN (uncut) airfoil section at the seed's eta, normalized to the high-lift main-foil
 * ingest convention (see {@link normalizeCleanFoil}): a raw Selig loop (upper-TE → LE → lower-TE),
 * unit chord, LE at [0, 0], aerodynamic UP positive. This is exactly the shape FoilPicker's `.dat`
 * upload feeds `conditionFoil(...)` → `setters.setMainCoords`, so the high-lift intake can set the
 * aircraft's own section as the base foil directly. OPTIONAL — old publishers omit it and the
 * intake falls back to the plain-flap mapping.
 */
export interface CleanFoilSeed {
  /** Selig-ordered [x, vert] loop: unit chord, LE at the origin, aero-up positive. */
  coords: [number, number][];
  /** Human name for the foil (shown as the loaded-foil identity). */
  name: string;
}

/** The seed record: the section itself + an optional human note for the design-label read-out. */
export interface SectionSeed {
  section: SlottedSection;
  /** The clean (uncut) section at the same eta, as a ready-to-ingest base foil. Optional. */
  cleanFoil?: CleanFoilSeed;
  note?: string;
}

/**
 * Normalize a clean airfoil section (upper/lower contours in the CAD's MODEL coordinates, both
 * ordered LE→TE — the /geometry/sections convention) into the high-lift main-foil ingest shape:
 * a raw Selig single-list loop (upper-TE → LE → lower-TE) at UNIT chord with the LE at [0, 0] and
 * aerodynamic UP positive.
 *
 * `upSign` is the aircraft-level z-orientation from the segmenter (+1 z-up, -1 z-down): a z-down
 * STEP's section has its camber bulging toward -z, so the vertical axis is FLIPPED when -1 to put
 * the aerodynamic upper surface on the positive side. (The upper/lower LABELS may swap under the
 * flip — irrelevant here, since the loop is re-split at ingest by `conditionFoil`, which detects
 * the true upper surface from the geometry.)
 *
 * Loops longer than `targetCount` are resampled by cumulative arclength to `targetCount` points
 * (the high-lift densify step splines through them anyway). Degenerate inputs (non-positive chord,
 * short/ragged contours, a non-finite LE) return `null` — honest-absent, never fabricated.
 */
export function normalizeCleanFoil(
  section: { upper: [number, number][]; lower: [number, number][] },
  chord: number,
  upSign: 1 | -1,
  targetCount = 121,
): [number, number][] | null {
  if (typeof chord !== 'number' || !Number.isFinite(chord) || chord <= 0) return null;
  const upper = Array.isArray(section.upper) ? section.upper : [];
  const lower = Array.isArray(section.lower) ? section.lower : [];
  if (upper.length < 8 || lower.length < 8) return null;
  const le = upper[0];
  if (!le || !Number.isFinite(le[0]) || !Number.isFinite(le[1])) return null;

  const push = (out: [number, number][], p: [number, number] | undefined): void => {
    if (!p || !Number.isFinite(p[0]) || !Number.isFinite(p[1])) return;
    out.push([(p[0] - le[0]) / chord, (upSign * (p[1] - le[1])) / chord]);
  };
  // Selig loop: upper TE→LE, then lower LE→TE (skip the shared LE node).
  const loop: [number, number][] = [];
  for (let i = upper.length - 1; i >= 0; i--) push(loop, upper[i]);
  for (let i = 1; i < lower.length; i++) push(loop, lower[i]);
  if (loop.length < 16) return null;
  return loop.length > targetCount ? resampleOpenPolyline(loop, targetCount) : loop;
}

/** Resample an OPEN polyline to `n` points by cumulative arclength (endpoints preserved). */
function resampleOpenPolyline(poly: [number, number][], n: number): [number, number][] {
  const cum = [0];
  for (let i = 1; i < poly.length; i++) {
    const a = poly[i - 1]!;
    const b = poly[i]!;
    cum.push(cum[i - 1]! + Math.hypot(b[0] - a[0], b[1] - a[1]));
  }
  const total = cum[cum.length - 1]! || 1;
  const out: [number, number][] = [];
  for (let k = 0; k < n; k++) {
    const s = (k / (n - 1)) * total;
    let j = 1;
    while (j < cum.length - 1 && cum[j]! < s) j++;
    const a = poly[j - 1]!;
    const b = poly[j]!;
    const t = (s - cum[j - 1]!) / (cum[j]! - cum[j - 1]! || 1);
    out.push([a[0] + t * (b[0] - a[0]), a[1] + t * (b[1] - a[1])]);
  }
  return out;
}

/** Minimal structural check that a parsed value is a usable {@link SectionSeed}. */
function isSectionSeed(value: unknown): value is SectionSeed {
  if (typeof value !== 'object' || value === null) return false;
  const section = (value as { section?: unknown }).section;
  if (typeof section !== 'object' || section === null) return false;
  const s = section as Partial<SlottedSection>;
  return (
    Array.isArray(s.elements) &&
    typeof s.chord === 'number' &&
    typeof s.eta === 'number' &&
    typeof s.name === 'string'
  );
}

/**
 * Publish a section seed for the high-lift tool to pick up on its next mount. One-shot: each
 * publish REPLACES any unconsumed previous seed. Best-effort (quota/SSR failures are swallowed).
 */
export function publishSectionSeed(seed: SectionSeed): void {
  try {
    if (typeof window === 'undefined') return;
    window.sessionStorage.setItem(SECTION_SEED_KEY, JSON.stringify(seed));
  } catch {
    // Best-effort: storage unavailable (SSR / private mode / quota) — the handoff simply no-ops.
  }
}

/**
 * Consume the pending section seed, if any. CLEARS the key first (one-shot — even a malformed
 * record is cleared so a poisoned value can never wedge the mount path), then parses + validates.
 * Returns `null` when there is no seed, storage is unavailable, or the record is malformed.
 */
export function consumeSectionSeed(): SectionSeed | null {
  try {
    if (typeof window === 'undefined') return null;
    const raw = window.sessionStorage.getItem(SECTION_SEED_KEY);
    if (raw == null) return null;
    window.sessionStorage.removeItem(SECTION_SEED_KEY);
    const parsed: unknown = JSON.parse(raw);
    return isSectionSeed(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/** The applied-seed record: the seed, the mode the user last selected, and (optionally) the LIVE
 *  config buffer at the last write. */
export interface AppliedSectionSeed {
  seed: SectionSeed;
  mode: 'aileron' | 'clean' | 'highlift' | 'twobody';
  /**
   * The LIVE config buffer persisted on the last write (remount-recovery). A shell remount used to
   * rebuild from the seed, silently discarding every device the user composed on top (added vane /
   * slotted flap / slat …). Persisting the buffer lets the intake restore the EXACT composed design
   * instead. Optional + back-compat: records written before this field (or from a v1 non-parametric
   * fallback) simply have no buffer and fall back to the seed rebuild.
   */
  buffer?: HighLiftAirfoil;
}

/** Minimal structural check that an untrusted value looks like a persisted {@link HighLiftAirfoil}. */
function looksLikeConfig(value: unknown): value is HighLiftAirfoil {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as { main?: unknown; design?: unknown; operation?: unknown };
  return (
    typeof v.main === 'object' && v.main !== null &&
    typeof v.design === 'object' && v.design !== null &&
    typeof v.operation === 'object' && v.operation !== null
  );
}

/** Persist the applied seed + mode (best-effort, same posture as publish). */
export function writeAppliedSeed(record: AppliedSectionSeed): void {
  try {
    if (typeof window === 'undefined') return;
    window.sessionStorage.setItem(APPLIED_SEED_KEY, JSON.stringify(record));
  } catch {
    // Best-effort - a failed write only costs remount resilience.
  }
}

/** Read the applied seed, if any. Malformed records are cleared and read as null. */
export function readAppliedSeed(): AppliedSectionSeed | null {
  try {
    if (typeof window === 'undefined') return null;
    const raw = window.sessionStorage.getItem(APPLIED_SEED_KEY);
    if (raw == null) return null;
    const parsed = JSON.parse(raw) as { seed?: unknown; mode?: unknown; buffer?: unknown };
    if (
      isSectionSeed(parsed?.seed) &&
      (parsed.mode === 'aileron' || parsed.mode === 'clean' || parsed.mode === 'highlift' || parsed.mode === 'twobody')
    ) {
      const record: AppliedSectionSeed = { seed: parsed.seed, mode: parsed.mode };
      // A plausible persisted buffer rides through; an old/absent/garbled buffer just falls back to
      // the seed rebuild (honest — never restore a half-parsed config).
      if (looksLikeConfig(parsed.buffer)) record.buffer = parsed.buffer;
      return record;
    }
    window.sessionStorage.removeItem(APPLIED_SEED_KEY);
    return null;
  } catch {
    return null;
  }
}

/** Drop the applied seed (the design no longer derives from the aircraft cut). */
export function clearAppliedSeed(): void {
  try {
    if (typeof window === 'undefined') return;
    window.sessionStorage.removeItem(APPLIED_SEED_KEY);
  } catch {
    // Best-effort.
  }
}

// ---------------------------------------------------------------------------
// Exact two-body view (the "two-body" seed mode)
// ---------------------------------------------------------------------------

/**
 * The seed section's EXACT cut contours (the wing box with its carved cove + the DEFLECTED control
 * surface), chord-normalized into the high-lift tool frame: x aft of the section LE in chords,
 * aerodynamic UP positive. This is READ-ONLY geometry — the exact import is displayed and measured
 * as-is, never resampled into parametric elements (which would fake a solvable config).
 */
export interface TwoBodySection {
  /** Wing-box contour (carved cove included), chord-normalized in the tool frame. */
  wing: [number, number][];
  /** DEFLECTED control-surface contour, same frame. */
  cs: [number, number][];
  /** Hinge point in the same frame, or `null` when the seed carries no finite hinge. */
  hinge: [number, number] | null;
  /** True when the section's vertical axis was FLIPPED to aero-up (a z-down source aircraft,
   *  detected by matching the wing box against the seed's clean foil — see {@link inferUpSign}). */
  flipped: boolean;
  /** Slot-geometry health of the exact cut (wing upstream, cs downstream; chord-normalized —
   *  the same {@link slotMetrics} vocabulary as the tool's own slots), or `null` if degenerate. */
  metrics: SlotMetrics | null;
}

/** Drop non-finite / ragged vertices from a stored contour (storage round-trips are untrusted). */
function finiteContour(contour: unknown): [number, number][] {
  if (!Array.isArray(contour)) return [];
  const out: [number, number][] = [];
  for (const p of contour) {
    if (!Array.isArray(p)) continue;
    const x = p[0];
    const y = p[1];
    if (typeof x === 'number' && typeof y === 'number' && Number.isFinite(x) && Number.isFinite(y)) {
      out.push([x, y]);
    }
  }
  return out;
}

/**
 * Infer the section's vertical orientation (+1 already aero-up, -1 needs the flip) by matching the
 * wing box against the seed's clean foil. The wing box FORWARD of the cut is exactly the clean
 * (uncut) foil at the same station, and the clean foil is published already aero-up — so whichever
 * orientation's upper/lower envelope tracks the clean envelope at forward stations is aero-up.
 * The seed record itself carries no z-orientation (the publisher's `upSign` flips only the clean
 * foil), which is why this is inferred rather than read. A symmetric section scores both
 * orientations equally and falls back to +1 (as-published) — for a mirror-symmetric foil the two
 * renders differ only in the deflection direction shown. No/short clean foil ⇒ +1.
 */
function inferUpSign(wing: [number, number][], clean: [number, number][] | undefined): 1 | -1 {
  if (!Array.isArray(clean) || clean.length < 8) return 1;
  const envelope = (
    pts: [number, number][],
    x0: number,
    halfW: number,
  ): { top: number; bot: number } | null => {
    let top = -Infinity;
    let bot = Infinity;
    for (const p of pts) {
      if (Math.abs(p[0] - x0) > halfW) continue;
      if (p[1] > top) top = p[1];
      if (p[1] < bot) bot = p[1];
    }
    return top >= bot ? { top, bot } : null;
  };
  // Forward stations only — well clear of the cove/cut region (hinges live aft of ~0.5c).
  let asIs = 0;
  let mirrored = 0;
  for (const x0 of [0.08, 0.15, 0.25, 0.35, 0.45]) {
    const w = envelope(wing, x0, 0.04);
    const c = envelope(finiteContour(clean), x0, 0.04);
    if (!w || !c) continue;
    asIs += Math.abs(w.top - c.top) + Math.abs(w.bot - c.bot);
    // Mirroring swaps + negates the envelope: top' = -bot, bot' = -top.
    mirrored += Math.abs(-w.bot - c.top) + Math.abs(-w.top - c.bot);
  }
  return mirrored < asIs ? -1 : 1;
}

/**
 * Normalize the seed's EXACT two-body cut contours into the high-lift tool frame: LE (the min-x
 * vertex over both bodies) to the origin, unit chord, aero-up positive (orientation matched
 * against the seed's clean foil — see {@link inferUpSign}). Also evaluates the slot-geometry
 * health of the exact cut on the tool's own {@link slotMetrics} vocabulary (wing upstream,
 * deflected cs downstream, all chord-normalized). Degenerate inputs (missing/short contours,
 * a non-positive chord) return `null` — honest-absent, never fabricated.
 */
export function normalizeTwoBodySection(seed: SectionSeed): TwoBodySection | null {
  const section = seed?.section;
  if (!section || !Number.isFinite(section.chord) || section.chord <= 0) return null;
  const elements = Array.isArray(section.elements) ? section.elements : [];
  const wingRaw = finiteContour(elements.find((e) => e?.id === 'wing')?.contour);
  const csRaw = finiteContour(elements.find((e) => e?.id === 'cs')?.contour);
  // Same >= 3-vertex floor `csSlotHealth` applies to a usable wall contour.
  if (wingRaw.length < 3 || csRaw.length < 3) return null;
  // LE datum: the min-x vertex over BOTH contours (the wing normally holds the true LE, but
  // scanning both keeps a pathological seed from shifting the frame).
  let le: [number, number] = wingRaw[0]!;
  for (const p of wingRaw) if (p[0] < le[0]) le = p;
  for (const p of csRaw) if (p[0] < le[0]) le = p;
  const chord = section.chord;
  const norm = (pts: [number, number][], sign: 1 | -1): [number, number][] =>
    pts.map((p) => [(p[0] - le[0]) / chord, (sign * (p[1] - le[1])) / chord]);
  const sign = inferUpSign(norm(wingRaw, 1), seed.cleanFoil?.coords);
  const wing = norm(wingRaw, sign);
  const cs = norm(csRaw, sign);
  const hx = section.hinge?.x;
  const hz = section.hinge?.z;
  const hinge: [number, number] | null =
    typeof hx === 'number' && typeof hz === 'number' && Number.isFinite(hx) && Number.isFinite(hz)
      ? [(hx - le[0]) / chord, (sign * (hz - le[1])) / chord]
      : null;
  const m = slotMetrics(wing as V2[], cs as V2[]);
  const metrics = Number.isFinite(m.gap) && Number.isFinite(m.overlap) ? m : null;
  return { wing, cs, hinge, flipped: sign === -1, metrics };
}
